/** Comments & Highlights collaborative panel.
 *
 * Displays all highlights and comments across the project with:
 * - Author attribution (who created it)
 * - Threaded replies (who replied)
 * - Resolution tracking (who resolved it)
 * - Soft-deletion audit (who deleted it)
 * - Jump-to-page navigation in the PDF viewer
 */

import { useState } from "react";
import type { Annotation } from "../types";

interface Props {
  annotations: Annotation[];
  activeDocument: string | null;
  onJumpToPage: (page: number) => void;
  onResolve: (id: number, resolved: boolean) => void;
  onReply: (id: number, content: string) => void;
  onDelete: (id: number) => void;
  onRestore: (id: number) => void;
  onRefresh: () => void;
}

type StatusFilter = "all" | "open" | "resolved" | "deleted";

function formatDate(isoStr: string | null): string {
  if (!isoStr) return "";
  try {
    const d = new Date(isoStr);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoStr;
  }
}

export function CommentsPanel({
  annotations,
  activeDocument,
  onJumpToPage,
  onResolve,
  onReply,
  onDelete,
  onRestore,
  onRefresh,
}: Props): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [docFilter, setDocFilter] = useState<"current" | "all">("current");
  const [searchQuery, setSearchQuery] = useState("");
  const [replyDrafts, setReplyDrafts] = useState<Record<number, string>>({});
  const [replyingId, setReplyingId] = useState<number | null>(null);

  // Filter items
  const filtered = annotations.filter((ann) => {
    // Document filter
    if (docFilter === "current" && activeDocument && ann.document !== activeDocument) {
      return false;
    }

    // Status filter
    if (statusFilter === "open" && (ann.is_deleted || ann.resolved)) return false;
    if (statusFilter === "resolved" && (ann.is_deleted || !ann.resolved)) return false;
    if (statusFilter === "deleted" && !ann.is_deleted) return false;

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchText = ann.selected_text.toLowerCase().includes(q);
      const matchComment = ann.comment.toLowerCase().includes(q);
      const matchAuthor = ann.username.toLowerCase().includes(q);
      const matchReplies = ann.replies.some(
        (r) => r.content.toLowerCase().includes(q) || r.username.toLowerCase().includes(q)
      );
      if (!matchText && !matchComment && !matchAuthor && !matchReplies) {
        return false;
      }
    }

    return true;
  });

  const handleSendReply = (annId: number) => {
    const text = (replyDrafts[annId] || "").trim();
    if (!text) return;
    onReply(annId, text);
    setReplyDrafts((prev) => ({ ...prev, [annId]: "" }));
    setReplyingId(null);
  };

  const openCount = annotations.filter((a) => !a.is_deleted && !a.resolved).length;
  const resolvedCount = annotations.filter((a) => !a.is_deleted && a.resolved).length;
  const deletedCount = annotations.filter((a) => a.is_deleted).length;

  return (
    <div className="comments-panel" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        className="comments-toolbar"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 12px",
          borderBottom: "1px solid var(--border)",
          background: "var(--bg-secondary)",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: 4 }}>
          <button
            className={statusFilter === "open" ? "primary" : ""}
            onClick={() => setStatusFilter("open")}
            style={{ fontSize: 11, padding: "2px 8px" }}
          >
            Open ({openCount})
          </button>
          <button
            className={statusFilter === "resolved" ? "primary" : ""}
            onClick={() => setStatusFilter("resolved")}
            style={{ fontSize: 11, padding: "2px 8px" }}
          >
            Resolved ({resolvedCount})
          </button>
          <button
            className={statusFilter === "deleted" ? "primary" : ""}
            onClick={() => setStatusFilter("deleted")}
            style={{ fontSize: 11, padding: "2px 8px" }}
          >
            Deleted ({deletedCount})
          </button>
          <button
            className={statusFilter === "all" ? "primary" : ""}
            onClick={() => setStatusFilter("all")}
            style={{ fontSize: 11, padding: "2px 8px" }}
          >
            All ({annotations.length})
          </button>
        </div>

        <span style={{ opacity: 0.4 }}>|</span>

        <div style={{ display: "flex", gap: 4 }}>
          <button
            className={docFilter === "current" ? "primary" : ""}
            onClick={() => setDocFilter("current")}
            style={{ fontSize: 11, padding: "2px 8px" }}
            title="Filter by active document"
          >
            Current Doc
          </button>
          <button
            className={docFilter === "all" ? "primary" : ""}
            onClick={() => setDocFilter("all")}
            style={{ fontSize: 11, padding: "2px 8px" }}
            title="Show all project documents"
          >
            All Docs
          </button>
        </div>

        <span style={{ flex: 1 }} />

        <input
          type="text"
          placeholder="Search comments, text, or authors..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            fontSize: 11,
            padding: "2px 8px",
            width: 180,
            borderRadius: 3,
            border: "1px solid var(--border)",
            background: "var(--bg-primary)",
            color: "var(--text-primary)",
          }}
        />

        <button onClick={onRefresh} style={{ fontSize: 11, padding: "2px 8px" }} title="Refresh annotations">
          ↻ Refresh
        </button>
      </div>

      <div
        className="comments-content"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {filtered.length === 0 ? (
          <div className="empty" style={{ textAlign: "center", padding: 32, opacity: 0.6 }}>
            No comments or highlights found in this view.
          </div>
        ) : (
          filtered.map((ann) => (
            <div
              key={ann.id}
              className={`comment-card ${ann.resolved ? "resolved" : ""} ${ann.is_deleted ? "deleted" : ""}`}
              style={{
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: "10px 14px",
                background: "var(--bg-primary)",
                borderLeft: `4px solid ${ann.color || "#ffeb3b"}`,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              {/* Header: page, document, author, dates, actions */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 6,
                  fontSize: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <button
                    onClick={() => onJumpToPage(ann.page)}
                    style={{
                      fontSize: 11,
                      padding: "1px 6px",
                      fontWeight: 600,
                      background: "var(--bg-secondary)",
                      border: "1px solid var(--border)",
                      borderRadius: 3,
                    }}
                    title="Jump to this page in PDF preview"
                  >
                    Page {ann.page}
                  </button>
                  {docFilter === "all" && (
                    <span style={{ fontSize: 11, opacity: 0.7 }}>
                      {ann.document.split("/").pop()}
                    </span>
                  )}
                  <span style={{ fontWeight: 600, color: "var(--accent)" }}>
                    @{ann.username}
                  </span>
                  <span style={{ opacity: 0.5, fontSize: 11 }}>
                    {formatDate(ann.created_at)}
                  </span>
                </div>

                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {!ann.is_deleted ? (
                    <>
                      <button
                        onClick={() => onResolve(ann.id, !ann.resolved)}
                        style={{
                          fontSize: 11,
                          padding: "1px 8px",
                          color: ann.resolved ? "var(--warning)" : "var(--success, #4caf50)",
                        }}
                      >
                        {ann.resolved ? "Reopen" : "✓ Resolve"}
                      </button>
                      <button
                        onClick={() => onDelete(ann.id)}
                        style={{ fontSize: 11, padding: "1px 6px", color: "var(--error)" }}
                        title="Delete this comment"
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => onRestore(ann.id)}
                      style={{ fontSize: 11, padding: "1px 8px" }}
                    >
                      ↺ Restore
                    </button>
                  )}
                </div>
              </div>

              {/* Selected quote if highlight */}
              {ann.selected_text && (
                <div
                  style={{
                    fontSize: 12,
                    fontStyle: "italic",
                    background: "var(--bg-secondary)",
                    padding: "4px 8px",
                    borderRadius: 4,
                    marginBottom: 6,
                    borderLeft: `2px solid ${ann.color || "#ffeb3b"}`,
                  }}
                >
                  “{ann.selected_text}”
                </div>
              )}

              {/* Comment body */}
              {ann.comment && (
                <div style={{ fontSize: 13, marginBottom: 8, lineHeight: 1.4 }}>
                  💬 {ann.comment}
                </div>
              )}

              {/* Resolution / Deletion Audit Banners */}
              {ann.resolved && (
                <div
                  style={{
                    fontSize: 11,
                    background: "rgba(76, 175, 80, 0.1)",
                    color: "var(--success, #4caf50)",
                    padding: "2px 8px",
                    borderRadius: 3,
                    marginBottom: 6,
                  }}
                >
                  ✓ Resolved by <strong>@{ann.resolved_by}</strong> on {formatDate(ann.resolved_at)}
                </div>
              )}

              {ann.is_deleted && (
                <div
                  style={{
                    fontSize: 11,
                    background: "rgba(244, 67, 54, 0.1)",
                    color: "var(--error)",
                    padding: "2px 8px",
                    borderRadius: 3,
                    marginBottom: 6,
                  }}
                >
                  🗑 Deleted by <strong>@{ann.deleted_by}</strong> on {formatDate(ann.deleted_at)}
                </div>
              )}

              {/* Threaded replies */}
              {ann.replies.length > 0 && (
                <div
                  style={{
                    borderTop: "1px solid var(--border)",
                    paddingTop: 6,
                    marginTop: 6,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    paddingLeft: 12,
                  }}
                >
                  {ann.replies.map((r) => (
                    <div
                      key={r.id}
                      style={{
                        fontSize: 12,
                        background: "var(--bg-secondary)",
                        padding: "4px 8px",
                        borderRadius: 4,
                      }}
                    >
                      <div style={{ display: "flex", gap: 6, marginBottom: 2 }}>
                        <strong style={{ color: "var(--accent)", fontSize: 11 }}>
                          @{r.username}
                        </strong>
                        <span style={{ opacity: 0.5, fontSize: 10 }}>
                          {formatDate(r.created_at)}
                        </span>
                      </div>
                      <div>{r.content}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply input */}
              {!ann.is_deleted && (
                <div style={{ marginTop: 8 }}>
                  {replyingId === ann.id ? (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input
                        type="text"
                        placeholder="Write a reply..."
                        value={replyDrafts[ann.id] || ""}
                        onChange={(e) =>
                          setReplyDrafts((prev) => ({ ...prev, [ann.id]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSendReply(ann.id);
                        }}
                        autoFocus
                        style={{
                          flex: 1,
                          fontSize: 12,
                          padding: "4px 8px",
                          borderRadius: 3,
                          border: "1px solid var(--border)",
                          background: "var(--bg-secondary)",
                          color: "var(--text-primary)",
                        }}
                      />
                      <button
                        className="primary"
                        onClick={() => handleSendReply(ann.id)}
                        style={{ fontSize: 11, padding: "4px 10px" }}
                      >
                        Reply
                      </button>
                      <button
                        onClick={() => setReplyingId(null)}
                        style={{ fontSize: 11, padding: "4px 8px" }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setReplyingId(ann.id)}
                      style={{
                        fontSize: 11,
                        padding: "2px 8px",
                        background: "transparent",
                        border: "1px dashed var(--border)",
                        borderRadius: 3,
                        opacity: 0.8,
                      }}
                    >
                      + Reply
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
